import * as React from 'react';
import { CmdModal, CmdModalFooterButton } from '@commander-services/gui-components';
import { useIntl } from 'react-intl';
import CmdIcon from '@commander-services/cmd-icon';
import dayjs from 'dayjs';
import { getLocaleNumber } from '../../../Services/LanguageService';
import useAnalytics from '../../../hooks/useAnalytics';

interface Co2EmissionProps {
  values: {
    avgCan: number | null;
    avgTheo: number | null;
    avgVehicle: number | null;
    valueCan: number | null;
    valueTheo: number | null;
  };
  datacyIndex: number;
  startDate: string;
  endDate: string;
  licenseNumber: string;
  vehicleName: string;
}
function Co2Emission(props: Co2EmissionProps) {
  const { formatMessage: f } = useIntl();
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const { trackEvent } = useAnalytics();

  const handleCancel = () => {
    setShowModal(false);
    trackEvent(['ride book', 'click', 'Ride book close CO2 emission modal']);
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'alarms.modal.rule.button.close' }),
      closeCallback: handleCancel,
    },
  ];

  const handleOpenModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setShowModal(true);
    trackEvent(['ride book', 'click', 'Ride book open CO2 emission modal']);
  };

  const getElement = () => {
    let element = <div />;
    const valueCan = `${Number(Number(props.values.valueCan).toFixed(1)).toLocaleString(
      getLocaleNumber()
    )} kg`;
    if (
      Number(props.values.avgVehicle) === Number(props.values.avgCan) ||
      props.values.avgVehicle === null
    ) {
      element = (
        <button
          type="button"
          data-cy={`row-${props.datacyIndex + 1}-co2`}
          className="b-eco-drive-info neutral w-full"
          onClick={handleOpenModal}
        >
          <span className="text-truncate" title={valueCan}>
            {valueCan}
          </span>
        </button>
      );
    } else if (Number(props.values.avgCan) > Number(props.values.avgVehicle)) {
      element = (
        <button
          data-cy={`row-${props.datacyIndex + 1}-co2`}
          className="b-eco-drive-info bad w-full"
          onClick={handleOpenModal}
        >
          <span className="text-truncate" title={valueCan}>
            {valueCan}
          </span>
        </button>
      );
    } else
      element = (
        <button
          data-cy={`row-${props.datacyIndex + 1}-co2`}
          className="b-eco-drive-info good w-full"
          onClick={handleOpenModal}
        >
          <span className="text-truncate" title={valueCan}>
            {valueCan}
          </span>
        </button>
      );
    return element;
  };

  const getColorCan = () => {
    if (
      Number(props.values.avgCan) === Number(props.values.avgVehicle) ||
      props.values.avgVehicle === null
    ) {
      return 'text-[#494949]';
    }
    if (Number(props.values.avgCan) > Number(props.values.avgVehicle)) {
      return 'text-[#b50536]';
    }
    return 'text-[#128169]';
  };

  const getColorTheo = () => {
    if (
      Number(props.values.avgTheo) === Number(props.values.avgVehicle) ||
      props.values.avgVehicle === null
    ) {
      return 'text-[#494949]';
    }
    if (Number(props.values.avgTheo) > Number(props.values.avgVehicle)) {
      return 'text-[#b50536]';
    }
    return 'text-[#128169]';
  };

  const getIconCan = () => {
    if (
      Number(props.values.avgCan) === Number(props.values.avgVehicle) ||
      props.values.avgVehicle === null
    ) {
      return '';
    }
    if (Number(props.values.avgCan) > Number(props.values.avgVehicle)) {
      return 'arrow-bold-up-from-line';
    }
    return 'arrow-bold-down-from-line';
  };

  const getIconTheo = () => {
    if (
      Number(props.values.avgTheo) === Number(props.values.avgVehicle) ||
      props.values.avgVehicle === null
    ) {
      return '';
    }
    if (Number(props.values.avgTheo) > Number(props.values.avgVehicle)) {
      return 'arrow-bold-up-from-line';
    }
    return 'arrow-bold-down-from-line';
  };

  const getIsIconCan = () => {
    if (
      Number(props.values.avgCan) === Number(props.values.avgVehicle) ||
      props.values.avgVehicle === null
    ) {
      return true;
    }
    return false;
  };

  const getIsIconTheo = () => {
    if (
      Number(props.values.avgTheo) === Number(props.values.avgVehicle) ||
      props.values.avgVehicle === null
    ) {
      return true;
    }
    return false;
  };

  return (
    <div
      className="align-middle text-right justify-content-end"
      style={{
        minWidth: '90px',
        maxWidth: '90px',
        flex: 1,
      }}
    >
      {getElement()}
      {showModal && (
        <CmdModal
          id="rides-info"
          title={f({ id: 'tripsOverview.modal.co2.title' })}
          footerButtons={buttons}
          width="810px"
          usePortal
          bodyPaddingClass="p-0"
          hideFooter
        >
          <div className="bg-gray-50 p-[15px] shadow-md w-full rounded-bl-md rounded-br-md">
            <div className="text-gray-700 text-[13px] font-medium mb-[5px]">
              {props.licenseNumber} - {props.vehicleName} (
              {dayjs(props.startDate).format('DD.MM.YYYY HH:mm')} -{' '}
              {dayjs(props.endDate).format('DD.MM.YYYY HH:mm')})
            </div>

            <div className="grid grid-cols-2 gap-4">
              <h2 className="text-base text-left">
                <strong>{f({ id: 'tripsOverview.modal.co2.can' })}</strong>
              </h2>
              <h2 className="text-base text-left">
                <strong>{f({ id: 'tripsOverview.modal.co2.theo' })}</strong>
              </h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-md shadow-xs">
                <div className="grid grid-cols-5 gap-4">
                  <div className="col-span-3">
                    <p className="text-gray-600 text-[13px] mb-0">
                      {f({ id: 'tripsOverview.modal.co2.total' })}
                    </p>
                  </div>
                  <div className="text-right col-span-2">
                    <p className="text-[16px] font-semibold mb-0">
                      {Number(Number(props.values.valueCan).toFixed(1)).toLocaleString(
                        getLocaleNumber()
                      )}
                      &nbsp;kg
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
                  <div className="col-span-3">
                    <p className="text-gray-600 text-[13px] mt-2 mb-0">
                      {f({ id: 'tripsOverview.modal.co2.avg' })}
                    </p>
                  </div>

                  <div className="text-right col-span-2">
                    <p className={`text-[16px] font-semibold ${getColorCan()} mt-2 mb-0`}>
                      {Number(Number(props.values.avgCan).toFixed(1)).toLocaleString(
                        getLocaleNumber()
                      )}
                      &nbsp;g/km
                      <CmdIcon
                        name={getIconCan()}
                        size={getIsIconCan() ? '9px' : undefined}
                        color={getIsIconCan() ? '#737373' : undefined}
                      />
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-md shadow-xs">
                <div className="grid grid-cols-5 gap-4">
                  <div className="col-span-3">
                    <p className="text-gray-600 text-[13px] mb-0">
                      {f({ id: 'tripsOverview.modal.co2.total' })}
                    </p>
                  </div>
                  <div className="text-right col-span-2">
                    <p className="text-[16px] font-semibold mb-0">
                      {Number(Number(props.values.valueTheo).toFixed(1)).toLocaleString(
                        getLocaleNumber()
                      )}
                      &nbsp;kg
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
                  <div className="col-span-3">
                    <p className="text-gray-600 text-[13px] mt-2 mb-0">
                      {f({ id: 'tripsOverview.modal.co2.avg' })}
                    </p>
                  </div>

                  <div className="text-right col-span-2">
                    <p className={`text-[16px] font-semibold ${getColorTheo()} mt-2 mb-0`}>
                      {Number(Number(props.values.avgTheo).toFixed(1)).toLocaleString(
                        getLocaleNumber()
                      )}
                      &nbsp;g/km
                      <CmdIcon
                        name={getIconTheo()}
                        size={getIsIconTheo() ? '9px' : undefined}
                        color={getIsIconTheo() ? '#737373' : undefined}
                      />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CmdModal>
      )}
    </div>
  );
}

export default Co2Emission;
